import React from "react";
import { BooleanFlag } from "./FeatureFlags";
import { useFeatureFlag } from "./FeatureFlagContext";

/**
 * Higher-order component that lets legacy class components consume a boolean feature flag.
 *
 * It subscribes to the feature-flag context via the `useFeatureFlag` hook, so the wrapped
 * component re-renders when the flag value changes at runtime (no page reload required).
 * The current boolean value is injected under `propName`.
 *
 * @param flag - The boolean flag to read.
 * @param propName - The prop name the flag value is injected under.
 * @returns A function that wraps a component and injects the flag value.
 */
export function withFeatureFlag<PropName extends string>(flag: BooleanFlag, propName: PropName) {
  return function wrap<OwnProps extends Record<PropName, boolean>>(Component: React.ComponentType<OwnProps>): React.FC<Omit<OwnProps, PropName>> {
    function WithFeatureFlag(props: Omit<OwnProps, PropName>) {
      const flagValue = useFeatureFlag(flag);
      const injected = { ...props, [propName]: flagValue } as unknown as OwnProps;
      return <Component {...injected} />;
    }
    WithFeatureFlag.displayName = `withFeatureFlag(${Component.displayName ?? Component.name ?? "Component"})`;
    return WithFeatureFlag;
  };
}
